import React, { useEffect, useMemo, useState } from 'react'
import { FormNames } from 'Src/constants/FormNames'
import { FieldNames } from 'Src/constants/FieldNames'
import { Col, Row } from 'antd'
import { authSlice } from 'Pages/auth'
import Form from 'antd/lib/form'
import moment from 'moment'
import { DATE_FORMAT_FOR_BACKEND } from 'Src/constants/Global'
import {
	LocalStorage,
	dictsSelector,
	Validators,
	ComponentPropsPartnerPaymentModal,
	optionsSelectNdsValue,
	TAXATION_IDS_TYPE,
} from 'Modules'
import { useAppDispatch, useFormConnector } from 'Src/hooks'
import { dashboardActions } from 'Pages/dashboard'
import { useSelector } from 'react-redux'
import { ChannelOrderPrefix } from 'Constants/Channel/channelOrderPrefix'
import {
	AccordionComponent,
	FormNotificationComponent,
	FormRowComponent,
	Heading,
	UITypes,
	RadioComponent,
	CheckboxComponent,
	SwitchComponent,
	DatePickerComponent,
	AccountNumberInput,
	BtnComponent,
	Indent,
	KbkInput,
	PaymentAccountInput,
	OktmoInput,
	InnInput,
	KppInput,
	CompanyNameForPaymentInput,
	FormRowElement,
	UISizes,
	BankBicInput,
	SelectComponent,
} from 'LibNeo'
import { noop } from 'Helpers'
import { RequiredHighlightText } from './styled'

const actions = {
	updatePartnerPayment: authSlice.actions.updatePartnerPayment,
	getDashboardDataAction: dashboardActions.getDashboardDataAction,
}
const PartnerPayment = ({ item, form, close }) => {
	useFormConnector(FormNames.PARTNER_PAYMENT, form)
	const { updatePartnerPayment, getDashboardDataAction } = useAppDispatch(actions)
	const taxationForm = useSelector(dictsSelector.getTaxationForm)

	const freeTaxBases = useSelector(dictsSelector.getFreeTaxBases)

	const optionsTaxionForm = [...taxationForm]?.sort((a, b) => (a.id > b.id ? 1 : -1))

	const optionsFreeTaxBases = freeTaxBases.map((value): any => {
		return {
			key: value?.id,
			value: value?.id,
			label: value.name,
		}
	})

	const isViewOsnBlockDefault = useMemo(
		(): boolean =>
			item?.taxation_form_id === TAXATION_IDS_TYPE.OSN &&
			Boolean(item?.free_tax_basis) &&
			Boolean(item?.free_tax_expiration_date),
		[item]
	)

	const isViewNdsPayerDefault = useMemo(
		(): boolean => item?.taxation_form_id === TAXATION_IDS_TYPE.USN && item?.nds_payer,
		[item]
	)

	const [viewBlockLater, setViewBlockLater] = useState<boolean>(false)
	const [applyButton, setApplyButton] = useState<boolean>(false)
	const [viewOsnBlock, setViewOsnBlock] = useState<boolean>(isViewOsnBlockDefault)
	const [viewNdsPayer, setViewNdsPayer] = useState<boolean>(isViewNdsPayerDefault)
	const [viewBudgetBlock, setViewBudgetBlock] = useState<boolean>(item?.budget_organization)
	const [freeTaxBase, setFreeTaxBases] = useState()

	useEffect((): void => {
		form.setFieldsValue({
			[FieldNames.FREE_TAX_BASIS]: viewOsnBlock
				? item?.[FieldNames.FREE_TAX_BASIS] || 'статья 145'
				: undefined,
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewOsnBlock])

	const getViewBlockBasic = (): boolean => {
		const taxionId =
			form.getFieldValue(FieldNames.TAXATION_FORM_ID) || item?.[FieldNames.TAXATION_FORM_ID]
		return taxationForm.find((value): boolean => value.id === taxionId)?.slug === 'osn'
	}

	const visibleAdditionalDetails = item?.channels?.some(
		(item) =>
			item.order_prefix === ChannelOrderPrefix.NKON || item.order_prefix === ChannelOrderPrefix.MFCN
	)

	const handleSubmit = (e: React.FormEvent<EventTarget>): void => {
		const { validateFields } = form
		e?.preventDefault()
		e?.stopPropagation()
		validateFields(async (err, values): Promise<void> => {
			if (!err) {
				updatePartnerPayment({
					...values,
					[FieldNames.FREE_TAX_EXPIRATION_DATE]: values[FieldNames.FREE_TAX_EXPIRATION_DATE]
						? values[FieldNames.FREE_TAX_EXPIRATION_DATE].format(DATE_FORMAT_FOR_BACKEND)
						: values[FieldNames.FREE_TAX_EXPIRATION_DATE] ?? null,
					[FieldNames.FREE_TAX_BASIS]: values[FieldNames.FREE_TAX_BASIS] ?? null,
					id: item?.id,
					[FieldNames.BUDGET_ORGANIZATION]:
						values[FieldNames.BUDGET_ORGANIZATION] ?? item?.budget_organization,
					[FieldNames.KBK]: values[FieldNames.KBK]?.length ? values[FieldNames.KBK] : null,
					[FieldNames.PAYMENT_ACCOUNT]: values[FieldNames.PAYMENT_ACCOUNT]?.length
						? values[FieldNames.PAYMENT_ACCOUNT]
						: null,
					[FieldNames.OKTMO]: values[FieldNames.OKTMO]?.length ? values[FieldNames.OKTMO] : null,
					[FieldNames.NAME_FOR_PAYMENT]: values[FieldNames.NAME_FOR_PAYMENT]?.length
						? values[FieldNames.NAME_FOR_PAYMENT]
						: null,
					[FieldNames.NDS_VALUE]: values[FieldNames.NDS_VALUE]
						? Number(values[FieldNames.NDS_VALUE].toString().replace('%', ''))
						: null,
					[FieldNames.NDS_PAYER]: Boolean(values[FieldNames.NDS_VALUE]),
				})
			}
			getDashboardDataAction()
		})
		if (viewBlockLater) {
			LocalStorage.setUserSettings({ partnerPaymentModalVisible: viewBlockLater })
		}
	}

	const handleSubmitSendLater = (e: React.FormEvent<EventTarget>): void => {
		e?.preventDefault()
		e?.stopPropagation()
		setViewBlockLater(!viewBlockLater)
		if (viewBlockLater) {
			LocalStorage.setUserSettings({ partnerPaymentModalVisible: viewBlockLater })
		}
		close()
	}

	const changeViewOsnBlock = (): void => {
		setViewOsnBlock(!viewOsnBlock)
	}

	const changeViewNdsPayer = (): void => {
		setViewNdsPayer((prev) => !prev)
	}

	const toggleBudgetOrganization = () => {
		form?.setFieldsValue({
			[FieldNames.PAYMENT_ACCOUNT]: null,
			[FieldNames.OKTMO]: null,
			[FieldNames.NAME_FOR_PAYMENT]: null,
			[FieldNames.KBK]: '',
		})
		setViewBudgetBlock(!viewBudgetBlock)
	}

	const isSendLater =
		form?.getFieldValue(FieldNames.KBK) &&
		form?.getFieldValue(FieldNames.OKTMO) &&
		form?.getFieldValue(FieldNames.NAME_FOR_PAYMENT) &&
		form?.getFieldValue(FieldNames.PAYMENT_ACCOUNT)

	const isWarningFreeTaxExpirationDate =
		form?.getFieldValue(FieldNames.FREE_TAX_EXPIRATION_DATE) &&
		new Date() > new Date(form?.getFieldValue(FieldNames.FREE_TAX_EXPIRATION_DATE))

	const taxionId =
		form?.getFieldValue(FieldNames.TAXATION_FORM_ID) || item?.[FieldNames.TAXATION_FORM_ID]

	const viewNdsValue =
		(taxionId === TAXATION_IDS_TYPE.OSN && !viewOsnBlock) ||
		(taxionId === TAXATION_IDS_TYPE.USN && viewNdsPayer)

	return (
		<div>
			<Heading type="h5">Сведения об организации</Heading>
			<Indent top={32} bottom={32}>
				<FormNotificationComponent
					type={UITypes.WARNING}
					title="Пожалуйста, обновите сведения"
					message="Это необходимо для подготовки шаблонов акта сдачи-приёмки услуг и выплаты агентского вознаграждения.
                В случае предоставления некорректных данных, Агент несет риск неблагоприятных для Принципала
                последствий,
                связанных с возможным предъявлением претензий Принципалу со стороны налоговых органов по вопросам
                исчисления и уплаты НДС."
					hideIcon
				/>
			</Indent>
			<>
				<Indent bottom={32}>
					<AccordionComponent id="tax_from" titleText="Система налогообложения" noExpand>
						<Indent top={16}>
							<FormRowComponent label="Форма налогообложения">
								<Indent top={8}>
									<FormRowElement size={UISizes.SMALL}>
										<SelectComponent
											fieldName={FieldNames.TAXATION_FORM_ID}
											form={form}
											items={optionsTaxionForm}
											fieldOptions={{
												initialValue: item?.[FieldNames.TAXATION_FORM_ID] || undefined,
											}}
										/>
									</FormRowElement>
								</Indent>
							</FormRowComponent>
						</Indent>
						{getViewBlockBasic() ? (
							<>
								<FormRowComponent label="Есть освобождение">
									<Indent top={8}>
										<SwitchComponent
											onChange={changeViewOsnBlock}
											checked={viewOsnBlock}
											title={viewOsnBlock ? 'Да' : 'Нет'}
											fieldOptions={{
												initialValue: viewOsnBlock,
											}}
										/>
									</Indent>
								</FormRowComponent>
								{viewOsnBlock && (
									<>
										<FormRowComponent label="Дата окончания действия">
											<Indent top={8}>
												<DatePickerComponent
													field={FieldNames.FREE_TAX_EXPIRATION_DATE}
													form={form}
													initialValue={
														item?.[FieldNames.FREE_TAX_EXPIRATION_DATE]
															? moment(item?.[FieldNames.FREE_TAX_EXPIRATION_DATE])
															: undefined
													}
													fieldOptions={{
														rules: [
															{
																validator: Validators.checkRequired({
																	required:
																		form.getFieldValue(FieldNames.TAXATION_FORM_ID) ===
																			TAXATION_IDS_TYPE.OSN &&
																		!!form.getFieldValue(FieldNames.FREE_TAX_BASIS),
																}),
															},
														],
													}}
													requiredHighlight={isWarningFreeTaxExpirationDate}
												/>
												{isWarningFreeTaxExpirationDate && (
													<RequiredHighlightText>Дата освобождения истекла</RequiredHighlightText>
												)}
											</Indent>
										</FormRowComponent>
										<FormRowComponent label="Основание">
											<Indent top={8}>
												<RadioComponent
													form={form}
													value={freeTaxBase}
													onChange={(value) => setFreeTaxBases(value)}
													name={FieldNames.FREE_TAX_BASIS}
													options={optionsFreeTaxBases}
													fieldOptions={{
														initialValue: item?.[FieldNames.FREE_TAX_BASIS] || undefined,
														rules: [
															{
																required: true,
																message: 'Поле обязательно для заполнения',
															},
														],
													}}
												/>
											</Indent>
										</FormRowComponent>
									</>
								)}
							</>
						) : (
							<FormRowComponent label="Являюсь плательщиком НДС">
								<Indent top={8}>
									<SwitchComponent
										form={form}
										fieldName={FieldNames.NDS_PAYER}
										onChange={changeViewNdsPayer}
										checked={viewNdsPayer}
										title={viewNdsPayer ? 'Да' : 'Нет'}
										fieldOptions={{
											initialValue: viewNdsPayer,
										}}
									/>
								</Indent>
							</FormRowComponent>
						)}
						{viewNdsValue && (
							<FormRowComponent label="Налоговая ставка">
								<Indent top={8}>
									<FormRowElement size={UISizes.SMALL}>
										<SelectComponent
											placeholder="Не выбрана"
											fieldName={FieldNames.NDS_VALUE}
											form={form}
											items={optionsSelectNdsValue}
											fieldOptions={{
												initialValue: item?.[FieldNames.NDS_VALUE]
													? `${item?.[FieldNames.NDS_VALUE]}%`
													: undefined,
												rules: [
													{
														validator: Validators.checkRequired({
															required: true,
														}),
													},
												],
											}}
										/>
									</FormRowElement>
								</Indent>
							</FormRowComponent>
						)}
					</AccordionComponent>
				</Indent>
				<AccordionComponent id="basic_information" titleText="Основная информация" noExpand>
					{item?.third_party_details && (
						<Indent top={16} bottom={16}>
							<FormNotificationComponent
								type={UITypes.INFO}
								message="Если вы нашли ошибку в реквизитах или необходимо внести изменения, сообщите
                                нам об этом на почту"
								actionLinkTitle="partners@dasreda.ru"
								onActionClick={noop}
							/>
						</Indent>
					)}
					<Indent top={16}>
						<FormRowComponent label="БИК" isString={item?.third_party_details}>
							<Indent top={8}>
								{item?.third_party_details ? (
									item?.[FieldNames.BIK]
								) : (
									<FormRowElement>
										{/* <SelectBik*/}
										{/*	initialValue={item?.[FieldNames.BIK]}*/}
										{/*	form={form}*/}
										{/*	disabled={item?.third_party_details}*/}
										{/* />*/}
										<BankBicInput
											initialValue={item?.[FieldNames.BIK]}
											form={form}
											disabled={item?.third_party_details}
											fieldName={FieldNames.BIK}
											label=""
										/>
									</FormRowElement>
								)}
							</Indent>
						</FormRowComponent>
					</Indent>
					<FormRowComponent label="Расчетный счёт" isString={item?.third_party_details}>
						<Indent top={8}>
							{item?.third_party_details ? (
								item?.[FieldNames.CHECKING_ACCOUNT]
							) : (
								<FormRowElement size={UISizes.MEDIUM} fixWidth>
									<AccountNumberInput
										form={form}
										required
										initialValue={item?.[FieldNames.CHECKING_ACCOUNT] || undefined}
										fieldName={FieldNames.CHECKING_ACCOUNT}
										disabled={item?.third_party_details}
										label=""
									/>
								</FormRowElement>
							)}
						</Indent>
					</FormRowComponent>
					{!item?.third_party_details && visibleAdditionalDetails && (
						<FormRowComponent label="Дополнительные реквизиты">
							<Indent top={8}>
								<SwitchComponent
									form={form}
									checked={viewBudgetBlock}
									onChange={toggleBudgetOrganization}
									fieldName={FieldNames.BUDGET_ORGANIZATION}
									title={viewBudgetBlock ? 'Да' : 'Нет'}
									fieldOptions={{
										initialValue: viewBudgetBlock,
									}}
								/>
							</Indent>
						</FormRowComponent>
					)}
					{visibleAdditionalDetails && (
						<>
							<FormRowComponent label="КБК" isString={item?.third_party_details}>
								{item?.third_party_details ? (
									item?.[FieldNames.KBK]
								) : (
									<FormRowElement>
										<KbkInput
											form={form}
											initialValue={item?.[FieldNames.KBK] || undefined}
											fieldName={FieldNames.KBK}
											disabled={!viewBudgetBlock}
											label=""
										/>
									</FormRowElement>
								)}
							</FormRowComponent>
							<FormRowComponent label="Лицевой счёт" isString={item?.third_party_details}>
								{item?.third_party_details ? (
									item?.[FieldNames.PAYMENT_ACCOUNT]
								) : (
									<FormRowElement>
										<PaymentAccountInput
											form={form}
											initialValue={item?.[FieldNames.PAYMENT_ACCOUNT] || undefined}
											fieldName={FieldNames.PAYMENT_ACCOUNT}
											disabled={!viewBudgetBlock}
											label=""
										/>
									</FormRowElement>
								)}
							</FormRowComponent>
							<FormRowComponent label="ОКТМО" isString={item?.third_party_details}>
								{item?.third_party_details ? (
									item?.[FieldNames.OKTMO]
								) : (
									<FormRowElement>
										<OktmoInput
											form={form}
											initialValue={item?.[FieldNames.OKTMO] || undefined}
											fieldName={FieldNames.OKTMO}
											disabled={!viewBudgetBlock}
											label=""
										/>
									</FormRowElement>
								)}
							</FormRowComponent>
							<FormRowComponent
								label="Наименование получателя платежа (для оплаты)"
								isString={item?.third_party_details}
							>
								{item?.third_party_details ? (
									item?.[FieldNames.NAME_FOR_PAYMENT]
								) : (
									<FormRowElement>
										<CompanyNameForPaymentInput
											form={form}
											initialValue={item?.[FieldNames.NAME_FOR_PAYMENT] || undefined}
											fieldName={FieldNames.NAME_FOR_PAYMENT}
											disabled={!viewBudgetBlock}
											label=""
										/>
									</FormRowElement>
								)}
							</FormRowComponent>
						</>
					)}
					{item?.third_party_details && (
						<>
							<FormRowComponent label="ИНН третьего лица" isString={item?.third_party_details}>
								{item?.third_party_details ? (
									item?.[FieldNames.THIRD_PARTY_VAT_NUMBER]
								) : (
									<FormRowElement>
										<InnInput
											form={form}
											required
											initialValue={item?.[FieldNames.THIRD_PARTY_VAT_NUMBER] || undefined}
											fieldName={FieldNames.THIRD_PARTY_VAT_NUMBER}
											label=""
										/>
									</FormRowElement>
								)}
							</FormRowComponent>
							<FormRowComponent label="КПП третьего лица" isString={item?.third_party_details}>
								{item?.third_party_details ? (
									item?.[FieldNames.THIRD_PARTY_KPP]
								) : (
									<FormRowElement>
										<KppInput
											form={form}
											required
											initialValue={item?.[FieldNames.THIRD_PARTY_KPP] || undefined}
											fieldName={FieldNames.THIRD_PARTY_KPP}
											label=""
										/>
									</FormRowElement>
								)}
							</FormRowComponent>
						</>
					)}
				</AccordionComponent>
			</>
			<Indent top={32}>
				<Row>
					<Col xs={24}>
						<div>
							<CheckboxComponent
								checked={applyButton}
								title="partnerPayment.apply_button"
								description="Ответственность за предоставление корректных данных, связанных с применением
                                Агентом системы налогообложения, в полном объёме несет Агент."
								onChange={(): void => setApplyButton(!applyButton)}
							/>
						</div>
					</Col>
				</Row>
			</Indent>
			<Indent top={32}>
				<Row gutter={[16, 0]} type="flex">
					<Col>
						<BtnComponent
							type={UITypes.PRIMARY}
							title="common.send"
							onClick={handleSubmit}
							disabled={!applyButton}
						/>
					</Col>
					{!item?.third_party_details && visibleAdditionalDetails && !isSendLater && (
						<Col>
							<BtnComponent
								type={UITypes.PASSIVE}
								title="Напомните позже"
								onClick={handleSubmitSendLater}
								disabled={!applyButton}
							/>
						</Col>
					)}
				</Row>
			</Indent>
		</div>
	)
}

export const PartnerPaymentModal = Form.create<ComponentPropsPartnerPaymentModal>({
	name: FormNames.PARTNER_PAYMENT,
})(PartnerPayment)
